// JobDetails.js
import {
  faDownload,
  faExpand,
  faHome,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Skeleton } from "@mui/material";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import HtmlRenderer from "../components/HtmlRenderer";
import ImageSlider from "../components/ImageSlider";
import {
  ApplyButton,
  ButtonContainer,
  ClearFix,
  ContentContainer,
  FactText,
  FullscreenIconContainer,
  HtmlRendererContainer,
  ImageContainer,
  OverlayImage,
  Section,
  SectionTitle,
  StyledButton,
  StyledIconWrapper,
} from "../components/StyledComponents";
import useFetchConfig from "../fetchFunction/ConfigFetch";
import useFetchSingleElements from "../fetchFunction/DetailsFetch";
import { getCompanyAccessName } from "../App";
import "../styles/JobsDetails.css";

const fallbackJob = {
  id: "demo-id",
  headlines: "Test-Job: React Developer (Remote)",
  textDetailSights: "<p>Dies ist eine statische Beschreibung.</p>",
  aboutUs: "Wir sind ein modernes Unternehmen...",
  exercises: "Entwicklung neuer Features\nCode Reviews durchführen",
  requirements: "Erfahrung mit React\nTeamfähigkeit",
  offers: "Home Office\nWeiterbildung",
  jobURLs: "",
  image_urls: [],
  exhibitionlocations: [],
  contactpersons: [],
  company: "Beispiel GmbH",
  employment_type: "Vollzeit",
  publication_date: "",
  salary_range: "",
  location: "Karlsruhe",
  iconUrl: ""
};

const JobDetails = ({ jobId, styling, companyAccessname, onClose }) => {
  const resolvedCompanyAccessname = companyAccessname || getCompanyAccessName();
  const { element: jobData, loading, error } = useFetchSingleElements(
    resolvedCompanyAccessname,
    "jobs",
    jobId
  );
  const { configSettings } = useFetchConfig();
  const theme = configSettings?.theme || {};

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  let job =
    Array.isArray(jobData) && jobData.length > 0
      ? jobData[0]
      : typeof jobData === "object" && jobData !== null
      ? jobData
      : fallbackJob;

  // map API response to expected frontend structure
  if (job.image_url && !job.image_urls?.length) {
    job.image_urls = [{ thumbnailUrl: job.image_url, fullscreenUrl: job.image_url }];
  }
  if (job.headline && !job.headlines) {
    job.headlines = job.headline;
  }
  if (job.text && !job.textDetailSights) {
    job.textDetailSights = job.text;
  }
  if (job.portal_url && !job.jobURLs) {
    job.jobURLs = job.portal_url;
  }

  const currentImage = job?.image_urls?.[currentImageIndex] || null;

  useEffect(() => {
    const widgetContent = document.querySelector(".unnpcw-container .widget-content");
    const overlay = document.querySelector(".mediaoverlay");
    if (widgetContent && overlay) {
      overlay.style.height = `${widgetContent.scrollHeight}px`;
      window.scrollTo(0, 0);
    }
  }, [isOverlayOpen]);

  const openOverlay = (imageUrl) => {
    setSelectedImageUrl(imageUrl);
    setImageLoaded(false);
    setIsOverlayOpen(true);
  };

  const closeOverlay = () => {
    setIsOverlayOpen(false);
    setSelectedImageUrl(null);
  };

  if (loading) {
    return (
      <div className="chapter-entry">
        <Skeleton variant="text" width="60%" height={30} />
        <Skeleton variant="rectangular" width="100%" height={200} />
        <Skeleton variant="text" width="100%" height={30} />
        <Skeleton variant="text" width="95%" height={30} />
        <Skeleton variant="text" width="90%" height={30} />
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "20px" }}>
          <StyledButton disabled variant="contained" color="primary">Jetzt bewerben</StyledButton>
          <StyledButton disabled variant="contained" color="primary">Zurück</StyledButton>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="chapter-entry">
        <p>Fehler beim Laden der Daten: {error || "Job nicht gefunden."}</p>
      </div>
    );
  }

  return (
    <div className="chapter-entry">
      <ContentContainer>
        <h3>{job.headlines || "Jobtitel nicht verfügbar"}</h3>
        {job.company && <FactText><strong>Unternehmen:</strong> {job.company}</FactText>}
        {job.location && <FactText><strong>Ort:</strong> {job.location}</FactText>}
        {job.employment_type && <FactText><strong>Art:</strong> {job.employment_type}</FactText>}
        {job.publication_date && <FactText><strong>Veröffentlicht:</strong> {job.publication_date}</FactText>}
        {job.salary_range && <FactText><strong>Gehalt:</strong> {job.salary_range}</FactText>}
      </ContentContainer>

      {job.image_urls?.length > 0 && (
        <ImageContainer>
          <ImageSlider
            images={job.image_urls.map((img) => ({
              src: img.thumbnailUrl || "",
              height: "300px",
            }))}
            onChange={setCurrentImageIndex}
          />
          {currentImage?.fullscreenUrl && (
            <FullscreenIconContainer onClick={() => openOverlay(currentImage.fullscreenUrl)}>
              <FontAwesomeIcon icon={faExpand} size="2x" />
            </FullscreenIconContainer>
          )}
        </ImageContainer>
      )}

      <HtmlRendererContainer>
        <HtmlRenderer
          texts={[job.textDetailSights || "Keine Beschreibung verfügbar"]}
          sanitizeHtml={true}
        />
      </HtmlRendererContainer>

      <ClearFix />

      {isOverlayOpen && (
        <div className="media-overlay" onClick={closeOverlay}>
          <div className="media-detail-container" onClick={(e) => e.stopPropagation()}>
            {!imageLoaded && (
              <>
                <Skeleton variant="text" width="100%" height={30} />
                <Skeleton variant="rectangular" width="100%" height={400} />
              </>
            )}
            {selectedImageUrl && (
              <OverlayImage
                src={selectedImageUrl}
                alt="Selected"
                style={{ display: imageLoaded ? "block" : "none" }}
                onLoad={() => setImageLoaded(true)}
              />
            )}
            {imageLoaded && (
              <div className="details">
                {currentImage?.description && (
                  <FactText styling={theme}><strong>Beschreibung:</strong> {currentImage.description}</FactText>
                )}
                {currentImage?.publisher && (
                  <FactText styling={theme}><strong>Herausgeber:</strong> {currentImage.publisher}</FactText>
                )}
                {currentImage?.dataname && (
                  <FactText styling={theme}><strong>Dateiname:</strong> {currentImage.dataname}</FactText>
                )}
                {currentImage?.copyright && (
                  <FactText styling={theme}><strong>Copyright:</strong> {currentImage.copyright}</FactText>
                )}
              </div>
            )}
            <ButtonContainer>
              <StyledButton styling={theme} variant="contained" color="secondary" onClick={closeOverlay}>Zurück</StyledButton>
              {currentImage?.fullscreenUrl && (
                <a href={currentImage.fullscreenUrl} target="_blank" rel="noopener noreferrer">
                  <StyledButton
                    styling={theme}
                    variant="contained"
                    color="secondary"
                    endIcon={<StyledIconWrapper icon={faDownload} styling={theme} />}
                  >
                    Download
                  </StyledButton>
                </a>
              )}
            </ButtonContainer>
          </div>
        </div>
      )}

      {job.aboutUs && (
        <Section>
          <SectionTitle styling={styling}><StyledIconWrapper icon={faHome} styling={styling} /> Über uns</SectionTitle>
          <p>{job.aboutUs}</p>
        </Section>
      )}

      {job.exercises && (
        <Section>
          <SectionTitle styling={styling}><StyledIconWrapper icon={faUser} styling={styling} /> Aufgaben</SectionTitle>
          {job.exercises.split("\n").map((line, index) => (
            <p key={index} style={{ paddingLeft: "20px", textIndent: "-20px" }}>{line}</p>
          ))}
        </Section>
      )}

      {job.requirements && (
        <Section>
          <SectionTitle styling={styling}><StyledIconWrapper icon={faUser} styling={styling} /> Anforderungen</SectionTitle>
          {job.requirements.split("\n").map((line, index) => (
            <p key={index} style={{ paddingLeft: "20px", textIndent: "-20px" }}>{line}</p>
          ))}
        </Section>
      )}

      {job.offers && (
        <Section>
          <SectionTitle styling={styling}><StyledIconWrapper icon={faUser} styling={styling} /> Was wir bieten</SectionTitle>
          {job.offers.split("\n").map((line, index) => (
            <p key={index} style={{ paddingLeft: "20px", textIndent: "-20px" }}>{line}</p>
          ))}
        </Section>
      )}

      {job.exhibitionlocations?.length > 0 && (
        <Section>
          <SectionTitle styling={styling}><StyledIconWrapper icon={faHome} styling={styling} /> Ausstellungsorte</SectionTitle>
          <p>{job.exhibitionlocations.join(", ")}</p>
        </Section>
      )}

      {job.contactpersons?.length > 0 && (
        <Section>
          <SectionTitle styling={styling}><StyledIconWrapper icon={faUser} styling={styling} /> Ansprechpartner</SectionTitle>
          <p>{job.contactpersons.join(", ")}</p>
        </Section>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "20px" }}>
        {job.jobURLs && (
          <ApplyButton
            styling={theme}
            variant="contained"
            color="secondary"
            onClick={() => (window.location.href = job.jobURLs)}
          >
            Jetzt bewerben
          </ApplyButton>
        )}
        <StyledButton className="small-button" styling={styling} variant="contained" color="primary" onClick={onClose}>
          Zurück
        </StyledButton>
      </div>
    </div>
  );
};

JobDetails.propTypes = {
  jobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  styling: PropTypes.object.isRequired,
  companyAccessname: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default JobDetails;
